import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wrench, Package } from 'lucide-react';

const fmtPrice = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(v);
};

/**
 * Compact teaser card for an "Ersatzteil / Modul" listing.
 * Slate/blue-grey accent. Emphasizes part number, OEM, compatibility and price.
 */
export const SparePartTeaserCard = ({ listing }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const photo = listing?.photos?.[0];
  const compat = (listing.compatible_machines || []).slice(0, 2).join(', ');

  return (
    <button
      type="button"
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      data-testid={`spare-teaser-${listing.listing_id}`}
      className="group relative text-left border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-900 dark:hover:border-slate-100 hover:shadow-sm transition-all duration-200 flex gap-3 p-3 focus:outline-none focus:ring-2 focus:ring-slate-700 dark:focus:ring-slate-300 focus:ring-offset-2 dark:focus:ring-offset-slate-950"
    >
      <div className="relative h-20 w-20 md:h-24 md:w-24 shrink-0 overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        {photo ? (
          <img
            src={photo}
            alt={listing.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-300 dark:text-slate-600">
            <Package className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5">
            <Wrench className="h-2.5 w-2.5" />
            {t('sparePart.card.label')}
          </span>
          {listing.part_number && (
            <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400 truncate" title={listing.part_number}>
              #{listing.part_number}
            </span>
          )}
        </div>
        <h3 className="font-manrope text-sm font-semibold text-slate-900 dark:text-slate-50 mt-1 leading-snug line-clamp-2">
          {listing.title}
        </h3>
        {compat ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
            {t('sparePart.card.compatible')} {compat}
            {listing.compatible_machines?.length > 2 ? ` +${listing.compatible_machines.length - 2}` : ''}
          </p>
        ) : listing.manufacturer_name ? (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{listing.manufacturer_name}</p>
        ) : null}
        <div className="mt-auto pt-1 flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {listing.quantity && listing.unit ? `${listing.quantity} ${listing.unit}` : t('sparePart.card.single')}
          </span>
          <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-50 tabular-nums">
            € {fmtPrice(listing.price_eur)}
          </span>
        </div>
      </div>
    </button>
  );
};
